import React, { useState, useEffect } from "react";
import {
  Minus,
  Plus,
  Trash2,
  ShoppingBag,
  MapPin,
  Clock,
  CreditCard,
} from "lucide-react";
import {
  CartItem,
  Address,
  SettingsResponse,
  LocationSettings,
  User,
  LoginResponse,
} from "../types";
import { createOrder, CreateOrderPayload, updateOrder } from "../api/order";
import { useTranslation } from "react-i18next";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { toast } from "sonner";
import {
  getDeliverySettings,
  DeliverySettings,
  getLocationSettings,
} from "../api/settings";
import { getItem, setItem } from "../utils/localStorage";
import {
  calculateDistance,
  formatAddress,
  MAX_SPEED,
  MIN_SPEED,
} from "../utils/utils";
import { getProfile } from "../api/auth";

interface CartProps {
  items: CartItem[];
  onRemoveItem: (id: string) => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onCheckout: (
    deliveryOption: "delivery" | "pickup",
    deliveryFee: number,
    orderId: string,
    deliveryAddress?: Address
  ) => void;
  totalPrice: number;
}

const Cart: React.FC<CartProps> = ({
  items,
  onRemoveItem,
  onUpdateQuantity,
  onCheckout,
  totalPrice,
}) => {
  const { t } = useTranslation();
  const [deliveryOption, setDeliveryOption] = useState<"delivery" | "pickup">(
    "delivery"
  );
  const [deliveryAddress, setDeliveryAddress] = useState<Address>({
    street: "",
    city: "",
    country: "",
    zipCode: "",
    latitude: 0,
    longitude: 0,
    fullAddress: "",
    province: "",
  });
  const [estimatedDistance, setEstimatedDistance] = useState(0); // km
  const [addressValue, setAddressValue] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deliverySettings, setDeliverySettings] =
    useState<DeliverySettings | null>(null);
  const [locationSettings, setLocationSettings] =
    useState<LocationSettings | null>(null);
  const [isNotPossibleToDelivery, setIsNotPossibleToDelivery] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      const { data, error } = await getDeliverySettings();
      if (data) {
        setDeliverySettings(data);
      } else {
        console.error("Failed to fetch delivery settings:", error);
        toast.error(error?.message);
      }
    };
    const fetchLocationSettings = async () => {
      const { data, error } = await getLocationSettings();
      console.log("fetchLocationSettings", data);
      if (data) {
        setLocationSettings(data);
      } else {
        console.error("Failed to fetch delivery settings:", error);
        toast.error(error?.message);
      }
    };

    fetchSettings();
    fetchLocationSettings();
  }, [t]);

  // Calcul automatique du tarif de livraison selon la distance
  const calculateDeliveryFee = (distance: number) => {
    if (deliverySettings) {
      return distance * deliverySettings.pricePerKm;
    }
    // Fallback to old logic if settings are not loaded
    if (distance <= 3) return 4.99;
    if (distance <= 7) return 7.99;
    return 12.99;
  };

  const deliveryFee =
    deliveryOption === "delivery" ? calculateDeliveryFee(estimatedDistance) : 0;
  const finalTotal = totalPrice + deliveryFee;

  console.log("ssssssssssssssssssss", deliveryFee);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAddressSelect = (value: any) => {
    setAddressValue(value);
    if (!value || !value.value || !value.value.place_id) {
      setDeliveryAddress({
        street: "",
        city: "",
        country: "",
        zipCode: "",
        latitude: 0,
        longitude: 0,
        fullAddress: "",
        province: "",
      });
      return;
    }
    const placeId = value.value.place_id;

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error("Google Maps JavaScript API script is not loaded.");
      toast.error("Could not connect to Google Maps service.");
      return;
    }

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );
    service.getDetails(
      {
        placeId: placeId,
        fields: ["address_component", "geometry.location", "formatted_address"],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry &&
          place.geometry.location &&
          place.address_components
        ) {
          const latLng = place.geometry.location;
          const addressComponents = place.address_components;

          const streetNumber =
            addressComponents.find((c) => c.types.includes("street_number"))
              ?.long_name || "";
          const route =
            addressComponents.find((c) => c.types.includes("route"))
              ?.long_name || "";
          const street = `${streetNumber} ${route}`.trim();
          const city =
            addressComponents.find((c) => c.types.includes("locality"))
              ?.long_name || "";
          const country =
            addressComponents.find((c) => c.types.includes("country"))
              ?.long_name || "";
          const zipCode =
            addressComponents.find((c) => c.types.includes("postal_code"))
              ?.long_name || "";

          const province =
            addressComponents.find((c) =>
              c.types.includes("administrative_area_level_1")
            )?.long_name || "";

          const newAddress: Address = {
            street,
            city,
            country,
            zipCode,
            latitude: latLng.lat(),
            longitude: latLng.lng(),
            fullAddress: place.formatted_address || value.label,
            province,
          };
          setDeliveryAddress(newAddress);

          console.log("New address", newAddress);

          const restaurantCoords = {
            latitude: locationSettings?.latitude || 0,
            longitude: locationSettings?.longitude || 0,
          };
          const destinationCoords = {
            latitude: latLng.lat(),
            longitude: latLng.lng(),
          };

          (async () => {
            try {
              const distanceInMeters = await calculateDistance(
                restaurantCoords,
                destinationCoords
              );
              if (distanceInMeters !== null) {
                const distanceInKm = distanceInMeters / 1000;
                const newEstimatedDistance = Math.round(distanceInKm * 10) / 10;
                setEstimatedDistance(newEstimatedDistance);

                if (
                  newEstimatedDistance === 0 ||
                  newEstimatedDistance >
                    (deliverySettings?.maxDeliveryDistanceKm ?? 0)
                ) {
                  console.log("Not possible to delivery");
                  setIsNotPossibleToDelivery(true);
                } else {
                  console.log("Possible to delivery");
                  setIsNotPossibleToDelivery(false);
                }
              }
            } catch (error) {
              console.error("Distance calculation failed:", error);
              toast.error(
                t("cart.errors.distance", "Could not calculate distance.")
              );
            }
          })();
        } else {
          console.error(`Error fetching place details. Status: ${status}`);
          toast.error(`Could not fetch address details. Status: ${status}`);
        }
      }
    );
  };

  const handleCheckout = async () => {
    if (deliveryOption === "delivery" && !addressValue) {
      toast.error(t("cart.addressRequired"));
      return;
    }
    setIsLoading(true);
    try {
      const orderItemsPayload = items.map((item) => ({
        dishId: item.dish.id,
        quantity: item.quantity,
      }));

      const payload: CreateOrderPayload = {
        items: orderItemsPayload,
        type: deliveryOption.toUpperCase() as "DELIVERY" | "PICKUP",
        address: deliveryOption === "delivery" ? deliveryAddress : undefined,
      };
      const currentOrderID = getItem("currentOrderID");
      console.log("currentOrderID", typeof currentOrderID, currentOrderID);
      let result = null;
      if (currentOrderID) {
        console.log("Creating order with payload", payload);
        result = await updateOrder(payload, currentOrderID as string);
      } else {
        console.log("Creating order with payload", payload);
        result = await createOrder(payload);
      }

      if (result.error) {
        toast.error(result.error.message);
        return;
      }

      const successMessage = currentOrderID
        ? t("order.orderUpdated")
        : t("order.orderCreated");
      toast.success(successMessage);

      setItem("currentOrderID", result.data!.id);

      console.log("Create order result", result);

      onCheckout(
        deliveryOption,
        deliveryFee,
        result.data!.id,
        deliveryOption === "delivery" ? deliveryAddress : undefined
      );
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      const simple = localStorage.getItem("user");
      const simpleUserData: LoginResponse | null = simple
        ? JSON.parse(simple)
        : null;

      const userId = simpleUserData?.id;
      if (!userId) {
        return;
      }

      const profileResponse = await getProfile(userId);

      console.log("profile response ", profileResponse);

      if (profileResponse.error) {
        toast.error(profileResponse.error.message);
      } else {
        const user = profileResponse.data;

        // Distance calculation
        const restaurantCoords = {
          latitude: locationSettings?.latitude || 0,
          longitude: locationSettings?.longitude || 0,
        }; // Montreal
        const destinationCoords = {
          latitude: user!.address.latitude,
          longitude: user!.address.longitude,
        };

        const distanceInMeters = await calculateDistance(
          restaurantCoords,
          destinationCoords
        );
        if (distanceInMeters !== null) {
          const distanceInKm = distanceInMeters / 1000;
          const newEstimatedDistance = Math.round(distanceInKm * 10) / 10;
          setEstimatedDistance(newEstimatedDistance);

          if (
            newEstimatedDistance === 0 ||
            newEstimatedDistance >
              (deliverySettings?.maxDeliveryDistanceKm ?? 0)
          ) {
            console.log("Not possible to delivery");
            setIsNotPossibleToDelivery(true);
          } else {
            console.log("Possible to delivery");
            setIsNotPossibleToDelivery(false);
          }
        }

        setDeliveryAddress({
          street: user!.address.street,
          city: user!.address.city,
          country: user!.address.country,
          zipCode: user!.address.zipCode,
          latitude: user!.address.latitude,
          longitude: user!.address.longitude,
          fullAddress: formatAddress(user!.address),
          province: user!.address.province || "",
        });
        setAddressValue({
          label: formatAddress(user!.address) as string,
          value: {
            place_id: "",
          },
        });
      }

      setIsLoading(false);
    };

    fetchAllData();
  }, [locationSettings]);

  if (items.length === 0) {
    return (
      <div className="text-center py-16">
        <ShoppingBag className="w-20 h-20 text-gray-300 mx-auto mb-6" />
        <h2 className="text-3xl font-bold text-black mb-4">
          {t("cart.emptyCart")}
        </h2>
        <p className="text-gray-600 text-lg">{t("cart.discoverMenu")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center mb-8">
        <ShoppingBag className="w-8 h-8 text-orange-500 mr-3" />
        <h1 className="text-3xl font-bold text-black">{t("cart.yourCart")}</h1>
        <span className="ml-4 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
          {items.length} {items.length > 1 ? t("cart.items") : t("cart.item")}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Liste des articles */}
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center space-x-4">
                <img
                  src={item.dish.imageUrl}
                  alt={item.dish.name}
                  className="w-24 h-24 object-cover rounded-xl"
                />

                <div className="flex-1">
                  <h3 className="font-bold text-black text-lg">
                    {item.dish.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {item.dish.description}
                  </p>
                  <div className="text-xl font-bold text-orange-500 mt-3">
                    {item.dish.price.toLocaleString()} CAD
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {/* Contrôles de quantité */}
                  <div className="flex items-center border border-gray-200 rounded-lg bg-gray-50">
                    <button
                      onClick={() =>
                        onUpdateQuantity(item.id, item.quantity - 1)
                      }
                      className="p-2 hover:bg-gray-200 transition-colors duration-200 rounded-l-lg"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-4 py-2 font-bold text-lg min-w-[3rem] text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() =>
                        onUpdateQuantity(item.id, item.quantity + 1)
                      }
                      className="p-2 hover:bg-gray-200 transition-colors duration-200 rounded-r-lg"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Bouton supprimer */}
                  <button
                    onClick={() => onRemoveItem(item.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{t("cart.subtotal")}:</span>
                  <span className="font-bold text-black text-lg">
                    ${(item.dish.price * item.quantity).toFixed(2)} CAD
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Résumé et options de livraison */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm sticky top-8">
            <h3 className="text-xl font-bold text-black mb-6">
              {t("cart.deliveryOptions")}
            </h3>

            {/* Options de livraison */}
            <div className="space-y-4 mb-6">
              <div
                className={`p-4 border-2 rounded-xl transition-all duration-200 ${
                  deliveryOption === "delivery"
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start">
                  <input
                    type="radio"
                    id="delivery-radio"
                    name="deliveryOption"
                    value="delivery"
                    checked={deliveryOption === "delivery"}
                    onChange={(e) => {
                      setDeliveryOption(e.target.value as "delivery");
                      if (
                        estimatedDistance === 0 ||
                        estimatedDistance >
                          (deliverySettings?.maxDeliveryDistanceKm ?? 0)
                      ) {
                        console.log("Not possible to delivery");
                        setIsNotPossibleToDelivery(true);
                      } else {
                        console.log("Possible to delivery");
                        setIsNotPossibleToDelivery(false);
                      }
                    }}
                    className="w-5 h-5 text-orange-500 mt-1"
                  />
                  <div className="ml-3 flex-1">
                    <label htmlFor="delivery-radio" className="cursor-pointer">
                      <div className="flex items-center">
                        <MapPin className="w-5 h-5 text-orange-500 mr-2" />
                        <span className="font-semibold text-black">
                          {t("cart.homeDelivery")}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {t("cart.deliveryTime")
                          .replace(
                            "{minTime}",
                            ((estimatedDistance / MAX_SPEED) * 60).toFixed(0)
                          )
                          .replace(
                            "{maxTime}",
                            ((estimatedDistance / MIN_SPEED) * 60).toFixed(0)
                          )}
                      </p>
                    </label>
                  </div>
                </div>
                {deliveryOption === "delivery" && (
                  <div className="mt-3 pl-8">
                    <GooglePlacesAutocomplete
                      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                      autocompletionRequest={{
                        componentRestrictions: {
                          country: ["ca"],
                        },
                        types: ["address"],
                      }}
                      selectProps={{
                        value: addressValue,
                        onChange: handleAddressSelect,
                        placeholder: t("cart.yourDeliveryAddress"),
                        styles: {
                          control: (base, state) => ({
                            ...base,
                            width: "100%",
                            borderWidth: "1px",
                            borderColor: state.isFocused
                              ? "#f97316"
                              : "#e5e7eb",
                            borderRadius: "0.5rem",
                            backgroundColor: state.isFocused
                              ? "#ffffff"
                              : "#f9fafb",
                            boxShadow: state.isFocused
                              ? "0 0 0 2px #f97316"
                              : "none",
                            "&:hover": {
                              borderColor: state.isFocused
                                ? "#f97316"
                                : "#d1d5db",
                            },
                          }),
                          input: (base) => ({
                            ...base,
                            margin: "0",
                            padding: "0",
                            height: "2.25rem",
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isFocused
                              ? "#fed7aa"
                              : state.isSelected
                              ? "#fb923c"
                              : "#ffffff",
                            color: state.isSelected ? "#ffffff" : "#1f2937",
                            "&:hover": {
                              backgroundColor: "#fed7aa",
                            },
                          }),
                          menu: (base) => ({
                            ...base,
                            borderRadius: "0.5rem",
                            overflow: "hidden",
                          }),
                        },
                        classNamePrefix: "react-select",
                      }}
                    />
                    <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                      <span>
                        {t("cart.distance")} {estimatedDistance} km
                      </span>
                      <span className="font-medium text-orange-600">
                        ${deliveryFee.toFixed(2)} CAD
                      </span>
                    </div>
                    {isNotPossibleToDelivery && estimatedDistance !== 0 && (
                      <p className="text-xs text-red-700 mt-2">
                        <span>{t("checkout.outOfDeliveryZone")}</span>
                        <span>{` ${deliverySettings?.maxDeliveryDistanceKm} km`}</span>
                      </p>
                    )}
                    {estimatedDistance === 0 && addressValue !== null && (
                      <p className="text-xs text-red-700 mt-2">
                        <span>{t("checkout.outOfDeliveryZoneAnswer")}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Retrait sur place */}
              <div
                className={`p-4 border-2 rounded-xl transition-all duration-200 ${
                  deliveryOption === "pickup"
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start">
                  <input
                    type="radio"
                    id="pickup-radio"
                    name="deliveryOption"
                    value="pickup"
                    checked={deliveryOption === "pickup"}
                    onChange={(e) => {
                      setDeliveryOption(e.target.value as "pickup");
                      setIsNotPossibleToDelivery(false);
                    }}
                    className="w-5 h-5 text-orange-500 mt-1"
                  />
                  <div className="ml-3 flex-1">
                    <label htmlFor="pickup-radio" className="cursor-pointer">
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 text-orange-500 mr-2" />
                        <span className="font-semibold text-black">
                          {t("cart.pickup")}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {t("cart.pickupTime")}
                      </p>
                    </label>
                    {deliveryOption === "pickup" && (
                      <div className="mt-3 text-xs text-gray-600">
                        {deliverySettings ? (
                          <table className="w-full">
                            <thead>
                              <tr>
                                <th className="text-start">
                                  {t("weekdays.days")}
                                </th>
                                <th className="text-start">
                                  {t("weekdays.hours")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {deliverySettings.deliverySlots
                                .filter((slot) => slot.isActive)
                                .sort(
                                  (a, b) =>
                                    [
                                      "MONDAY",
                                      "TUESDAY",
                                      "WEDNESDAY",
                                      "THURSDAY",
                                      "FRIDAY",
                                      "SATURDAY",
                                      "SUNDAY",
                                    ].indexOf(a.dayOfWeek) -
                                    [
                                      "MONDAY",
                                      "TUESDAY",
                                      "WEDNESDAY",
                                      "THURSDAY",
                                      "FRIDAY",
                                      "SATURDAY",
                                      "SUNDAY",
                                    ].indexOf(b.dayOfWeek)
                                )
                                .map((slot) => (
                                  <tr key={slot.id}>
                                    <td className="font-semibold capitalize">
                                      {t(
                                        `weekdays.${slot.dayOfWeek.toLowerCase()}`,
                                        slot.dayOfWeek.toLowerCase()
                                      )}
                                    </td>
                                    <td>
                                      {slot.pickupSchedule.startTime.slice(
                                        0,
                                        5
                                      )}{" "}
                                      -{" "}
                                      {slot.pickupSchedule.endTime.slice(0, 5)}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        ) : (
                          <p>{t("cart.loadingHours", "Loading hours...")}</p>
                        )}
                        <p className="mt-2 text-green-600 font-medium">
                          {t("cart.free")}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Résumé de la commande */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="font-semibold text-black mb-4">
                {t("cart.orderSummary")}
              </h4>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t("cart.subtotal")}</span>
                  <span className="font-medium">
                    ${totalPrice.toFixed(2)} CAD
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {deliveryOption === "delivery"
                      ? t("cart.deliveryFee")
                      : t("cart.pickup")}
                  </span>
                  <span className="font-medium">
                    {deliveryOption === "delivery"
                      ? `${deliveryFee.toFixed(2)} CAD`
                      : t("cart.free")}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-black">
                      {t("cart.total")}
                    </span>
                    <span className="text-2xl font-bold text-orange-500">
                      ${finalTotal.toFixed(2)} CAD
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={isLoading || isNotPossibleToDelivery}
                className={`w-full bg-orange-500 text-white py-4 rounded-xl font-bold text-lg hover:bg-orange-600 transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 ${
                  isLoading || isNotPossibleToDelivery
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <CreditCard className="w-5 h-5" />
                )}
                <span>
                  {isLoading
                    ? t("cart.processingOrder")
                    : t("cart.proceedToPayment")}
                </span>
              </button>

              <p className="text-xs text-gray-500 text-center mt-3">
                {t("cart.securePayment")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
